import React, { useMemo } from "react";
import CountUp from "react-countup";
import BaseChart from "../../components/ui/BaseChart";

const getColor = (pct) => {
  if (pct >= 75) return "#16A34A";
  if (pct >= 40) return "#D97706";
  return "#DC2626";
};

const TargetProgress = ({ data }) => {
  const {
    year = new Date().getFullYear(),
    target = 5000,
    achieved = 0,
    lastYearAchieved = 0,
    percentage = 0,
    remaining = 0,
    expectedByNow = 0,
    onTrack = false,
  } = data ?? {};

  const color = getColor(percentage);
  const clampedPct = Math.min(percentage, 100);

  const gap = Math.max(expectedByNow - achieved, 0);
  const future = Math.max(target - Math.max(expectedByNow, achieved), 0);

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "pie",
        height: 240,
        margin: [0, 0, 10, 0],
        backgroundColor: "transparent",
        style: { fontFamily: "Poppins, sans-serif" },
        events: {
          render() {
            const chart = this;
            const center = chart.series?.[0]?.center;
            if (!center) return;
            const cx = chart.plotLeft + center[0];
            const cy = chart.plotTop + center[1] - 12;
            const pctText = `${Math.round(clampedPct)}%`;

            if (chart._gaugePct) {
              chart._gaugePct.attr({ text: pctText, x: cx, y: cy }).css({ color });
              chart._gaugeLbl.attr({ x: cx, y: cy + 18 });
            } else {
              chart._gaugePct = chart.renderer
                .text(pctText, cx, cy)
                .css({ fontSize: "26px", fontWeight: "800", color })
                .attr({ align: "center", zIndex: 5 })
                .add();
              chart._gaugeLbl = chart.renderer
                .text("of annual target", cx, cy + 18)
                .css({ fontSize: "9px", color: "#9ca3af", letterSpacing: "0.05em" })
                .attr({ align: "center", zIndex: 5 })
                .add();
            }
          },
        },
      },
      title: { text: null },
      tooltip: {
        formatter() {
          const valueMap = {
            "Expected by Now": expectedByNow,
            "Remaining": remaining,
          };
          const value = valueMap[this.point.name] ?? this.point.y;
          return `<b>${this.point.name}</b>: ${value?.toLocaleString()} WOs`;
        },
        backgroundColor: "#1e293b",
        style: { color: "#f8fafc" },
        borderWidth: 0,
        borderRadius: 6,
      },
      plotOptions: {
        pie: {
          startAngle: -90,
          endAngle: 90,
          center: ["50%", "82%"],
          size: "165%",
          innerSize: "68%",
          dataLabels: {
            enabled: true,
            distance: -28,
            style: { fontSize: "9px", fontWeight: "700", textOutline: "none" },
          },
          showInLegend: false,
          borderWidth: 3,
          borderColor: "#fff",
          states: { hover: { brightness: 0.04 } },
        },
      },
      series: [
        {
          name: "Target",
          data: [
            { name: "Achieved", y: achieved, color, dataLabels: { enabled: false } },
            {
              name: "Expected by Now",
              y: gap,
              color: "#cbd5e1",
              dataLabels: {
                enabled: gap > 0,
                formatter() { return expectedByNow.toLocaleString(); },
                color: "#475569",
              },
            },
            {
              name: "Remaining",
              y: future,
              color: "#f1f5f9",
              dataLabels: {
                enabled: future > 0,
                formatter() { return remaining.toLocaleString(); },
                color: "#94a3b8",
              },
            },
          ],
        },
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [achieved, gap, future, expectedByNow, remaining, color, clampedPct],
  );

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="font-semibold text-gray-800 text-sm">Annual Target</h3>
          <p className="text-[11px] text-gray-400 mt-0.5">
            {year} goal: <CountUp start={0} end={target} duration={1.5} separator="," /> WOs
          </p>
        </div>
        <span
          className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${
            onTrack ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"
          }`}
        >
          <i className={`fa-solid ${onTrack ? "fa-check" : "fa-exclamation"} mr-1 text-[9px]`} />
          {onTrack ? "On Track" : "Behind"}
        </span>
      </div>

      {/* Highcharts semi-circle gauge */}
      <BaseChart options={chartOptions} className="w-full" />

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 mt-1 pt-3 border-t border-gray-50">
        {[
          { label: "Achieved", value: achieved, highlight: true },
          { label: "Remaining", value: remaining },
          { label: "Target", value: target },
        ].map(({ label, value, highlight }) => (
          <div key={label} className="flex flex-col items-center gap-0.5">
            <span className={`text-base font-bold ${highlight ? "text-primary" : "text-gray-700"}`}>
              <CountUp start={0} end={value ?? 0} duration={1.5} separator="," />
            </span>
            <span className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</span>
          </div>
        ))}
      </div>

      {/* Context */}
      <div className="mt-3 space-y-1.5">
        <div className="flex justify-between text-[11px] text-gray-400">
          <span>Expected by now</span>
          <span className="font-semibold text-gray-600">
            <CountUp start={0} end={expectedByNow} duration={1.5} separator="," />
          </span>
        </div>

        {/* Dual progress bar: achieved vs expected */}
        <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="absolute h-full rounded-full transition-all duration-700"
            style={{ width: `${Math.min(percentage, 100)}%`, backgroundColor: color }}
          />
          {/* Expected-by-now marker */}
          {target > 0 && (
            <div
              className="absolute top-0 h-full w-0.5 bg-gray-400 opacity-60"
              style={{ left: `${Math.min((expectedByNow / target) * 100, 100)}%` }}
            />
          )}
        </div>

        <div className="flex justify-between text-[11px] text-gray-400">
          <span>Last year total</span>
          <span className="font-semibold text-gray-600">
            <CountUp start={0} end={lastYearAchieved} duration={1.5} separator="," />
          </span>
        </div>
      </div>
    </div>
  );
};

export default TargetProgress;
